/**
 * <s-product-gallery> — main image track with clickable thumbnails.
 *
 * Thumbnails are buttons with commandfor="<gallery id>" command="--show" data-index="n".
 * The component scrolls the [data-gallery-track] to slide n and keeps aria-current
 * on the matching thumbnail in sync when the track is swiped or scrolled.
 *
 * Required markup:
 *   [data-gallery-track]          — horizontally scroll-snapping slide container.
 *   [data-gallery-slide]          — one per media item, direct children of the track.
 *   button[data-index][command]   — thumbnails targeting this element's id.
 */
class SProductGallery extends HTMLElement {
  #ac;
  #observer;

  connectedCallback() {
    this.#ac = new AbortController();
    this.addEventListener('command', this.#handleCommand, { signal: this.#ac.signal });

    const track = this.querySelector('[data-gallery-track]');
    if (!track) return;

    // Track the slide that is mostly in view so swipes update the thumbnail state.
    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
            const index = Number(entry.target.getAttribute('data-gallery-slide'));
            this.#setCurrent(index);
          }
        }
      },
      { root: track, threshold: [0.6] }
    );
    for (const slide of track.querySelectorAll('[data-gallery-slide]')) {
      this.#observer.observe(slide);
    }
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
  }

  #handleCommand = (event) => {
    if (event.command !== '--show') return;
    const index = Number(event.source?.dataset.index ?? 0);
    const track = this.querySelector('[data-gallery-track]');
    const slide = track?.querySelector(`[data-gallery-slide="${index}"]`);
    if (!track || !slide) return;

    const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
    const isRtl = getComputedStyle(track).direction === 'rtl';
    const offset = isRtl ? -(track.scrollWidth - slide.offsetLeft - slide.offsetWidth) : slide.offsetLeft;
    track.scrollTo({ left: offset, behavior: reduceMotion ? 'auto' : 'smooth' });
    this.#setCurrent(index);
  };

  #setCurrent(index) {
    for (const thumb of this.querySelectorAll('button[data-index]')) {
      if (Number(thumb.dataset.index) === index) {
        thumb.setAttribute('aria-current', 'true');
      } else {
        thumb.removeAttribute('aria-current');
      }
    }
  }
}

if (!customElements.get('s-product-gallery')) {
  customElements.define('s-product-gallery', SProductGallery);
}
